import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';

import {
	Box,
	Tabs,
	Tab,
	Tooltip,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
} from '@mui/material';

import ContainerLoadingIndicator from 'src/components/common/loading/ContainerLoadingIndicator';
import {
	getAnnotationTemplates,
	ANNOTATION_TEMPLATE_LOADING_KEY,
} from 'src/features/annotation_template/store/annotationTemplateThunks';
import QuestionnaireForm from 'src/features/questionnaire/components/QuestionnaireForm';
import {
	a11yProps,
	CustomTabPanel,
} from 'src/features/questionnaire/pages/QuestionnairePage';
import { QUESTIONNAIRE_LOADING_KEY } from 'src/features/questionnaire/store';
import { IQuestionnaireTemplate } from 'src/features/questionnaire_template/domain/types';
import { IAssignedUser } from 'src/features/study_assignment/domain/types';
import { AppDispatch } from 'src/store';

interface IAnnotationTemplatePanelProps {
	studyAssignmentId?: number;
	assignedUsers?: IAssignedUser[];
	isFormDisabled: boolean;
}

const AnnotationTemplatePanel: React.FC<IAnnotationTemplatePanelProps> = ({
	studyAssignmentId,
	assignedUsers,
	isFormDisabled,
}) => {
	const dispatch = useDispatch<AppDispatch>();
	const { siteSlug } = useParams<{ siteSlug: string }>();
	const [searchParams, setSearchParams] = useSearchParams();

	const isTemplateLoading = useSelector(
		(state: { loading: ILoadingState }) => {
			return state.loading.loadingStates[ANNOTATION_TEMPLATE_LOADING_KEY];
		},
	);
	const isQuestionnaireLoading = useSelector(
		(state: { loading: ILoadingState }) => {
			return state.loading.loadingStates[QUESTIONNAIRE_LOADING_KEY];
		},
	);

	const [activeTemplateId, setActiveTemplateId] = useState<string | null>();
	const [annotationTemplates, setAnnotationTemplates] = useState<
		IQuestionnaireTemplate[] | null
	>(null);
	const [hasLoadedTemplates, setHasLoadedTemplates] = useState(false);
	const [selectedAssignmentId, setSelectedAssignmentId] = useState<number>();

	const assignees = useMemo(
		() =>
			(assignedUsers ?? []).filter(
				(user) => user.study_assignment_id != null,
			),
		[assignedUsers],
	);
	const resolvedAssignmentId =
		studyAssignmentId ?? selectedAssignmentId ?? undefined;

	const tabsRef = useRef<HTMLDivElement>(null);

	const handleChange = (
		event: React.SyntheticEvent | undefined,
		newValue: string,
	) => {
		setActiveTemplateId(newValue);

		const newParams = new URLSearchParams(searchParams);
		newParams.set('annotation_template_id', newValue);
		setSearchParams(newParams);
	};

	useEffect(() => {
		if (studyAssignmentId) {
			setSelectedAssignmentId(studyAssignmentId);
			return;
		}

		setSelectedAssignmentId((current) => {
			if (
				current &&
				assignees.some((user) => user.study_assignment_id === current)
			) {
				return current;
			}
			return assignees[0]?.study_assignment_id ?? undefined;
		});
	}, [studyAssignmentId, assignees]);

	useEffect(() => {
		if (!siteSlug) return;

		dispatch(
			getAnnotationTemplates({
				siteSlug: siteSlug,
				requestParams: {
					sort: 'index',
					order: 'asc',
					only_published: true,
				},
				onSuccess: (response) => {
					setHasLoadedTemplates(true);
					setAnnotationTemplates(response);
					if (response.length > 0) {
						const templateId = searchParams.get(
							'annotation_template_id',
						);

						const isValidId = response.some(
							(template) => String(template.id) === templateId,
						);

						if (isValidId && templateId) {
							setActiveTemplateId(templateId);
						} else {
							handleChange(undefined, String(response[0].id));
						}
					}
				},
				onError: () => setHasLoadedTemplates(true),
			}),
		);
	}, [siteSlug]);

	useEffect(() => {
		if (tabsRef.current) {
			const selectedTab = tabsRef.current.querySelector(
				`[role="tab"][aria-selected="true"]`,
			);
			if (selectedTab) {
				(selectedTab as HTMLElement).scrollIntoView({
					behavior: 'smooth',
					inline: 'start',
					block: 'nearest',
				});
			}
		}
	}, [activeTemplateId]);

	return (
		<>
			{!hasLoadedTemplates || isTemplateLoading ? (
				<Box sx={{ position: 'relative', minHeight: 120 }}>
					<ContainerLoadingIndicator
						loadingKey={ANNOTATION_TEMPLATE_LOADING_KEY}
						manualLoading
					/>
				</Box>
			) : !annotationTemplates?.length ? (
				<Box sx={{ p: 3 }}>No annotation template available.</Box>
			) : (
				activeTemplateId && (
					<>
						{assignees.length > 0 && (
							<FormControl fullWidth size='small' sx={{ mt: 1 }}>
								<InputLabel id='annotation-assignee-label'>
									Assigned user
								</InputLabel>
								<Select
									labelId='annotation-assignee-label'
									label='Assigned user'
									value={resolvedAssignmentId ?? ''}
									onChange={(event) =>
										setSelectedAssignmentId(
											Number(event.target.value),
										)
									}
								>
									{assignees.map((user) => (
										<MenuItem
											key={user.study_assignment_id}
											value={user.study_assignment_id!}
										>
											{`${user.first_name} ${user.last_name}`}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						)}
						<Box
							sx={{
								position: 'sticky',
								top: 0,
								backgroundColor: 'white',
								zIndex: 100,
								borderBottom: 1,
								borderColor: 'divider',
							}}
						>
							<Tabs
								ref={tabsRef}
								value={activeTemplateId}
								onChange={handleChange}
								aria-label='annotation template tabs'
								variant='scrollable'
								scrollButtons='auto'
							>
								{annotationTemplates.map((template) => (
									<Tab
										key={template.id}
										value={String(template.id)}
										label={
											<Tooltip
												title={template.name}
												arrow
											>
												<span
													style={{
														display: 'inline-block',
														maxWidth: 180,
														overflow: 'hidden',
														textOverflow:
															'ellipsis',
														whiteSpace: 'nowrap',
														fontSize: '0.75rem',
													}}
												>
													{template.name}
												</span>
											</Tooltip>
										}
										disabled={
											isQuestionnaireLoading &&
											searchParams.get(
												'annotation_template_id',
											) !== String(template.id)
										}
										{...a11yProps(
											`${template.name}-${template.id}`,
										)}
									/>
								))}
							</Tabs>
						</Box>
						{annotationTemplates.map((template) => (
							<CustomTabPanel
								key={template.id}
								value={activeTemplateId}
								index={String(template.id)}
							>
								{activeTemplateId && resolvedAssignmentId ? (
									<QuestionnaireForm
										key={resolvedAssignmentId}
										questionnaireId={Number(
											activeTemplateId,
										)}
										studyAssignmentId={resolvedAssignmentId}
										isFormDisabled={isFormDisabled}
										compactMode={true}
									/>
								) : (
									<Box sx={{ p: 3 }}>
										No study assignment available.
									</Box>
								)}
							</CustomTabPanel>
						))}
					</>
				)
			)}
		</>
	);
};

export default AnnotationTemplatePanel;
